<script setup lang="ts">
  import { computed } from 'vue'
  import { fmt, randomId } from '@/functions'

  const props = defineProps<{
    id?: string,
    modelValue: string,
    type?: string,
    label?: string,
    placeholder?: string,
    required?: boolean,
    readonly?: boolean,
    error?: string | string[],
    maxLength?: number,
    rows?: number,
    disabled?: boolean,
    autocomplete?: string,
    list?: string,
    tabindex?: number,
    loading?: boolean,
  }>()

  const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void,
    (e: 'keydown', event: KeyboardEvent): void,
    (e: 'focus', event: FocusEvent): void,
  }>()

  const value = computed({
    set: (val) => {
      emit('update:modelValue', val)
    },
    get: () => props.modelValue,
  })

  const onInput = (event: Event) => {
    const eventTarget = event.target as HTMLInputElement
    emit('update:modelValue', eventTarget.value)
  }
  const onFocus = (event: FocusEvent) => {
    emit('focus', event)
  }

  const defaultId = randomId()
  const inputId = computed(() => props.id ?? defaultId)

  defineOptions({
    inheritAttrs: false,
  })
</script>

<template>
  <div
    class="app-input"
    v-bind="$attrs">
    <label
      v-if="label"
      :for="inputId"
      class="form-label">
      {{ label }}
      <span
        v-if="required"
        class="text-danger-600">
        *
      </span>
    </label>
    <div class="relative flex items-center">
      <textarea
        v-if="type === 'textarea'"
        :id="inputId"
        class="form-input min-h-20"
        :disabled="disabled"
        :value="value"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        :rows="rows ?? 3"
        :required="required"
        :readonly="readonly"
        :maxlength="maxLength"
        :list="list"
        :tabindex="tabindex"
        @input="onInput"
        @focus="onFocus"></textarea>
      <input
        v-else
        :id="inputId"
        class="form-input"
        :disabled="disabled"
        :autocomplete="autocomplete"
        :type="type"
        :value="value"
        :placeholder="placeholder"
        :required="required"
        :readonly="readonly"
        :maxlength="maxLength"
        :tabindex="tabindex"
        :list="list"
        @input="onInput"
        @focus="onFocus"
        @keydown="emit('keydown', $event)">
      <div class="absolute flex items-center text-xs text-gray-600 z-10 right-2 gap-1">
        <fa-icon
          v-if="loading"
          icon="i-far-circle-notch"
          class="animate-spin" />
      </div>
      <div
        v-if="maxLength !== undefined"
        class="absolute bottom-0.5 right-1 bg-inherit text-2.5 text-gray-500">
        {{ fmt.number(maxLength - value.length) }}
      </div>
      <slot name="inline"></slot>
    </div>
    <slot></slot>
    <app-error-msg :msg="error" />
  </div>
</template>

<style scoped>
  input[list]::-webkit-calendar-picker-indicator,
  input[list]::-webkit-list-button {
    display: none !important;
  }

  .text-center .form-input {
    text-align: center;
  }
</style>
